
import React from 'react';
import { SystemConfig } from '../../types';
import { Printer, Power, PowerOff, AlertCircle } from 'lucide-react';
import { handleSilentPrint } from '../../services/silentPrinter';

interface Props {
  config: SystemConfig;
  setConfig: (config: SystemConfig) => void;
}

export const SettingsTabPrinter: React.FC<Props> = ({ config, setConfig }) => {
  const printerConfig = config.printerConfig || {
    enabled: false,
    width: '80mm',
    autoPrintOnAccept: true,
    printMode: 'default',
    manualPrinterName: '',
    promptBeforeManualPrint: true,
  };

  const updatePrinterConfig = (updates: any) => {
    setConfig({
      ...config,
      printerConfig: {
        ...printerConfig,
        ...updates
      }
    });
  };

  const handleTestPrint = () => {
    const mockOrder: any = {
      id: "9876",
      customerName: "Cliente Teste",
      address: "Rua Exemplo, 123",
      neighborhood: "Centro",
      orderValue: 45.90,
      deliveryFee: 5.00,
      paymentMethod: "Cartão de Crédito",
      items: [
        { quantity: 1, name: "X-Salada", price: 25.00 },
        { quantity: 1, name: "Coca-Cola 2L", price: 15.90 }
      ],
      createdAt: Date.now(),
      type: 'DELIVERY'
    };

    handleSilentPrint(mockOrder, config);
  };

  const SectionHeader = ({ icon: Icon, title, subTitle, color = 'text-status-info' }: any) => (
    <div className="flex items-center gap-3 border-b border-border-dim pb-4 mb-6">
      <div className={`p-2.5 bg-card-bg border border-border-dim rounded-xl ${color}`}><Icon className="w-5 h-5" /></div>
      <div><h4 className="font-black text-text-primary uppercase tracking-widest text-sm">{title}</h4><p className="text-xs text-text-muted font-bold uppercase tracking-tighter">{subTitle}</p></div>
    </div>
  );

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300 pb-28">

      {/* HEADER PRINCIPAL DA ABA */}
      <div>
        <h3 className="text-3xl font-black text-text-primary mb-1 uppercase tracking-tighter">Configurações de Impressão</h3>
        <p className="text-sm text-text-muted font-medium">Gerencie as preferências para impressão de cupons.</p>
      </div>

      <section className="bg-card-bg p-6 rounded-3xl border border-border-dim shadow-xl relative overflow-hidden group">
        <SectionHeader icon={Printer} title="Habilitar Impressão" subTitle="Ativa o sistema de impressão silenciosa" color="text-status-info" />
        <div className="flex items-center justify-between relative z-10">
          <p className="text-base text-text-secondary">Ativar impressão de cupons</p>
          <button
            onClick={() => updatePrinterConfig({ enabled: !printerConfig.enabled })}
            className={`relative w-12 h-6 rounded-full p-1 transition-colors ${printerConfig.enabled ? 'bg-sal-cyber-teal' : 'bg-border-strong'}`}
          >
            <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${printerConfig.enabled ? 'translate-x-6' : 'translate-x-0'}`} />
          </button>
        </div>
      </section>

      {printerConfig.enabled && (
        <section className="bg-card-bg p-6 rounded-3xl border border-border-dim shadow-xl relative overflow-hidden group space-y-6">

          <SectionHeader icon={Printer} title="Largura da Bobina" subTitle="Defina o formato do cupom" color="text-status-info" />
          <div className="grid grid-cols-2 gap-3 relative z-10">
            <button
              onClick={() => updatePrinterConfig({ width: '80mm' })}
              className={`p-3 rounded-xl border flex items-center justify-center gap-2 transition-all ${printerConfig.width === '80mm'
                ? 'bg-sal-cyber-teal/20 border-sal-cyber-teal text-sal-cyber-teal'
                : 'bg-bg-main/50 border-border-dim text-text-muted hover:bg-bg-main'
                }`}
            >
              <span className="font-bold">80mm</span>
              <span className="text-sm opacity-60">(Padrão)</span>
            </button>
            <button
              onClick={() => updatePrinterConfig({ width: '58mm' })}
              className={`p-3 rounded-xl border flex items-center justify-center gap-2 transition-all ${printerConfig.width === '58mm'
                ? 'bg-sal-cyber-teal/20 border-sal-cyber-teal text-sal-cyber-teal'
                : 'bg-bg-main/50 border-border-dim text-text-muted hover:bg-bg-main'
                }`}
            >
              <span className="font-bold">58mm</span>
              <span className="text-sm opacity-60">(Pequena)</span>
            </button>
          </div>

          <SectionHeader icon={Power} title="Modo de Impressão" subTitle="Como o cupom será impresso" color="text-status-info" />
          <div className="grid grid-cols-2 gap-3 relative z-10">
            <button
              onClick={() => updatePrinterConfig({ printMode: 'default' })}
              className={`p-3 rounded-xl border flex items-center justify-center gap-2 transition-all ${printerConfig.printMode === 'default'
                ? 'bg-sal-cyber-teal/20 border-sal-cyber-teal text-sal-cyber-teal'
                : 'bg-bg-main/50 border-border-dim text-text-muted hover:bg-bg-main'
                }`}
            >
              <span className="font-bold">Padrão do Sistema</span>
            </button>
            <button
              onClick={() => updatePrinterConfig({ printMode: 'manual_selection' })}
              className={`p-3 rounded-xl border flex items-center justify-center gap-2 transition-all ${printerConfig.printMode === 'manual_selection'
                ? 'bg-sal-cyber-teal/20 border-sal-cyber-teal text-sal-cyber-teal'
                : 'bg-bg-main/50 border-border-dim text-text-muted hover:bg-bg-main'
                }`}
            >
              <span className="font-bold">Seleção Manual</span>
            </button>
          </div>

          {printerConfig.printMode === 'manual_selection' && (
            <div className="space-y-4 relative z-10 bg-bg-main/30 p-5 rounded-2xl border border-border-dim">
              <div>
                <label htmlFor="manualPrinterName" className="text-xs font-black text-text-muted uppercase tracking-widest ml-1 block mb-2">Nome da Impressora</label>
                <input
                  id="manualPrinterName"
                  type="text"
                  value={printerConfig.manualPrinterName || ''}
                  onChange={(e) => updatePrinterConfig({ manualPrinterName: e.target.value })}
                  placeholder="Ex: Minha Impressora Térmica"
                  className="w-full bg-input-bg border border-input-border rounded-2xl px-5 py-4 text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-sal-cyber-teal"
                />
                <p className="text-sm text-text-muted mt-1">Informe o nome da sua impressora térmica.</p>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-base text-text-secondary mb-1">Avisar antes de Imprimir Manualmente</p>
                  <p className="text-sm text-text-muted">Exibe uma mensagem para confirmar a seleção da impressora.</p>
                </div>
                <button
                  onClick={() => updatePrinterConfig({ promptBeforeManualPrint: !printerConfig.promptBeforeManualPrint })}
                  className={`relative w-12 h-6 rounded-full p-1 transition-colors ${printerConfig.promptBeforeManualPrint ? 'bg-sal-cyber-teal' : 'bg-border-strong'}`}
                >
                  <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${printerConfig.promptBeforeManualPrint ? 'translate-x-6' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>
          )}

          <SectionHeader icon={Printer} title="Impressão Automática" subTitle="Comportamento de impressão" color="text-status-info" />
          <div className="flex items-center justify-between relative z-10">
            <p className="text-base text-text-secondary">Imprimir ao Aceitar Pedido</p>
            <button
              onClick={() => updatePrinterConfig({ autoPrintOnAccept: !printerConfig.autoPrintOnAccept })}
              className={`relative w-12 h-6 rounded-full p-1 transition-colors ${printerConfig.autoPrintOnAccept ? 'bg-sal-cyber-teal' : 'bg-border-strong'}`}
            >
              <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${printerConfig.autoPrintOnAccept ? 'translate-x-6' : 'translate-x-0'}`} />
            </button>
          </div>

          <div className="pt-6 border-t border-border-dim mt-6">
            <button
              onClick={handleTestPrint}
              className="w-full py-4 bg-sal-cyber-teal text-badge-text-inv font-black rounded-2xl shadow-lg hover:brightness-110 active:scale-95 transition-all uppercase tracking-widest text-sm flex items-center justify-center gap-2"
            >
              <Printer className="w-4 h-4" />
              Imprimir Cupom de Teste
            </button>
            <p className="text-sm text-center text-text-muted mt-3">
              {printerConfig.printMode === 'default'
                ? 'Certifique-se de que sua impressora térmica é a impressora padrão do sistema operacional.'
                : `Ao clicar em imprimir, selecione "${printerConfig.manualPrinterName || 'Sua Impressora Térmica'}" na janela de impressão.`
              }
            </p>
          </div>
        </section>
      )}
    </div>
  );
};
